import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { useLanguage } from '../Layout/LanguageContext';
import { FaArrowRight, FaPiggyBank } from 'react-icons/fa';

const FundingTeaserSection = styled.section`
  padding: 4rem 2rem;
  background: var(--background-color);

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Banner = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  border-radius: 20px;
  padding: 3rem;
  display: flex;
  align-items: center;
  gap: 2rem;
  color: white;
  box-shadow: 0 16px 48px rgba(0, 51, 102, 0.2);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, var(--accent-color), var(--accent-light));
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2.5rem 1.5rem;
    gap: 1.5rem;
  }
`;

const IconWrap = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--primary-color);
  background: white;
`;

const Body = styled.div`
  flex: 1;
`;

const Eyebrow = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--accent-light);
  margin-bottom: 0.5rem;
`;

const Title = styled.h2`
  font-size: 1.9rem;
  font-weight: 700;
  margin: 0 0 0.6rem 0;
  color: white;
  line-height: 1.25;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Text = styled.p`
  font-size: 1.05rem;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  max-width: 640px;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  background: var(--accent-color);
  color: var(--primary-dark);
  padding: 0.9rem 1.8rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent-light);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  }
`;

const teaserContent = {
  de: {
    eyebrow: 'Förderung',
    title: 'Bis zu 50 % Förderung für Ihr Projekt',
    text: 'Beratungs- und Digitalisierungsleistungen können über die BAFA-Beratungsförderung oder den Digitalbonus Bayern bezuschusst werden – bis zu 30.000 € je Vorhaben.',
    button: 'Fördermöglichkeiten ansehen'
  },
  en: {
    eyebrow: 'Funding',
    title: 'Up to 50% funding for your project',
    text: 'Consulting and digitalization services can be subsidized through the BAFA consulting grant or the Bavarian Digitalbonus – up to €30,000 per project.',
    button: 'Explore funding options'
  }
};

const FundingTeaser: React.FC = () => {
  const { language } = useLanguage();
  const t = teaserContent[language];
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  return (
    <FundingTeaserSection id="funding">
      <Banner
        ref={ref}
        initial={{ opacity: 0, y: 40 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <IconWrap>
          <FaPiggyBank />
        </IconWrap>
        <Body>
          <Eyebrow>{t.eyebrow}</Eyebrow>
          <Title>{t.title}</Title>
          <Text>{t.text}</Text>
        </Body>
        <CTAButton to="/foerderung">
          {t.button}
          <FaArrowRight />
        </CTAButton>
      </Banner>
    </FundingTeaserSection>
  );
};

export default FundingTeaser;
